import styled from "styled-components";
import type { StyledTrProps } from "@/types/DataTableTypes";

export const Row = styled.div<StyledTrProps>`
  .td:first-child {
    ${(props) =>
      props.$isShowDecoratorColor
        ? `border-left: 5px solid ${props.$decoratorColor || "transparent"}`
        : ""};
    ${(props) => (props.$isShowDecoratorColor ? "padding-left: 0.25rem" : "")};
  }
`;

// --snt-table-bg is provided by the nearest ancestor that sets it (e.g. SaveViewPage
// sets it to the page bg so the sticky bar blends in); falls back to white.
export const BottomBar = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 6;
  background-color: var(--snt-table-bg, #ffffff);
`;

export const Styles = styled.div`
  position: relative;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: #fff;

  /* Shared row styles for both header and body */
  .tr {
    padding: 0 !important;

    &.odd {
      background: #f9fafa;
      .td {
        background: #f9fafa;
      }
    }
    &.even {
      background: #fff;
      .td {
        background: #fff;
      }
    }
  }

  .mh-30 {
    min-height: 30px;
    background-color: white;
  }

  .th {
    font-weight: 500;
    border-bottom: 2px solid #f3f5f6;
    padding: 0.5rem;
    background-color: white;

    .resizer {
      display: inline-block;
      width: 10px;
      height: 100%;
      position: absolute;
      right: 0;
      top: 0;
      z-index: 1;
      touch-action: none;
      border-left: 1px solid #424b59 !important;
      cursor: e-resize !important;
      opacity: 0;

      :hover {
        opacity: 0.5;
      }

      &.isResizing {
        opacity: 0.5;
      }
    }

    .group-header-icon {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #eff3f4;
    }

    .sortIcon {
      color: grey;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #eff3f4;
    }

    .sortIconHover {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #eff3f4;
      opacity: 0;
    }

    &:hover {
      .table-group {
        opacity: 1;
      }

      .sortIcon {
        color: unset;
      }

      .sortIconHover {
        opacity: 1;
      }
    }
    .table-group {
      display: inline-block;
      opacity: 0;
      padding: 0px 0.5rem;
      &.show {
        opacity: 1;
      }

      .table-group-toggle {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: #eff3f4;
      }

      .table-group-menu > .table-group-menu-divider:last-of-type {
        display: none;
      }
    }
  }

  /* Sticky header - outside .table-container so CSS sticky works */
  .table-sticky-header {
    position: sticky;
    top: var(--sticky-header-top, 56px);
    z-index: 10;
    background-color: white;
    overflow: visible;
    border-top-left-radius: 3px;
    border-top-right-radius: 3px;
  }

  /* --sticky-header-top clears the app's fixed top bar, which only overlaps the
     table when the page itself is the scrollport. In a dialog the scrollport is
     .modal-body (SntDialog always renders modal-dialog-scrollable) and its top
     already sits below that bar. Sticky shifts a box DOWN whenever its natural
     position is nearer the scrollport top than the offset demands - no scroll
     needed - so the bar-sized offset lifted the header out of its slot, leaving
     a blank band above the table and painting it over the rows (STIC-16002).
     Pin it to the top of the modal body instead. */
  .modal-body & .table-sticky-header {
    top: 0;
  }

  .table {
    border-spacing: 0;
    .tbody {
      position: relative;
      z-index: 1;
    }

    .tr {
      /* Override Bootstrap's .table > :not(caption) > * > * padding on rows */
      padding: 0 !important;

      &.odd {
        background: #f9fafa;
        .td {
          background: #f9fafa;
        }
      }
      &.even {
        background: #fff;
        .td {
          background: #fff;
        }
      }
    }

    .mh-30 {
      min-height: 30px;
      background-color: white;
    }

    .th {
      font-weight: 500;
      border-bottom: 2px solid #f3f5f6;
      padding: 0.5rem;
      background-color: white;

      .resizer {
        display: inline-block;
        width: 10px;
        height: 100%;
        position: absolute;
        right: 0;
        top: 0;
        z-index: 1;
        /* prevents from scrolling while dragging on touch devices */
        touch-action: none;
        border-left: 1px solid #424b59 !important;
        cursor: e-resize !important;
        opacity: 0;

        :hover {
          opacity: 0.5;
        }

        &.isResizing {
          opacity: 0.5;
        }
      }

      .group-header-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: #eff3f4;
      }

      .sortIcon {
        color: grey;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: #eff3f4;
      }

      .sortIconHover {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background-color: #eff3f4;
        opacity: 0;
      }

      &:hover {
        .table-group {
          opacity: 1;
        }

        .sortIcon {
          color: unset;
        }

        .sortIconHover {
          opacity: 1;
        }
      }
      .table-group {
        display: inline-block;
        opacity: 0;
        padding: 0px 0.5rem;
        &.show {
          opacity: 1;
        }

        .table-group-toggle {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: #eff3f4;
        }

        .table-group-menu > .table-group-menu-divider:last-of-type {
          display: none;
        }
      }
    }

    .td {
      word-break: break-word;
    }
  }
`;
